/**
 * The page behind the QR code on a door.
 *
 * Readable by anyone with the code — so it shows the room and its free/busy
 * shape and nothing else. Booking requires signing in; a code on a wall is
 * not an authorisation.
 */
import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useMutation, useQuery } from "@tanstack/react-query";

import { Icon } from "@/components/Icon";
import { Endorsement, Lockup } from "@/components/Logo";
import { ErrorState, Notice, SkeletonRows } from "@/components/States";
import { StatusPill } from "@/components/StatusPill";
import { ApiError, api } from "@/lib/api";
import { formatTime } from "@/lib/format";
import { useSession } from "@/lib/session";
import type { StatusToken } from "@/lib/types";

interface PublicRoom {
  room: {
    name: string;
    code: string;
    capacity: number;
    floor: string | null;
    amenities: string[];
    location_name: string;
    timezone: string;
  };
  display: { state: string; status_token: StatusToken; label: string; subline: string };
  busy: { start_at: string; end_at: string }[];
  booking_enabled: boolean;
  quick_durations: number[];
  signed_in: boolean;
  sign_in_required: boolean;
  server_time: string;
}

export function QrRoom() {
  const { token } = useParams<{ token: string }>();
  const { user, loading } = useSession();
  const [booked, setBooked] = useState<{ end_at: string; pending: boolean } | null>(null);
  const [error, setError] = useState<ApiError | null>(null);

  const query = useQuery({
    // The session is restored from the refresh cookie a moment after the page
    // loads. Keying on the user makes the view re-read once that lands, so a
    // signed-in employee is not asked to sign in again.
    queryKey: ["qr-room", token, user?.id ?? null],
    queryFn: () => api.get<PublicRoom>(`/public/rooms/${token}`),
    enabled: !loading,
    refetchInterval: 30_000,
  });

  const book = useMutation({
    mutationFn: (minutes: number) =>
      api.post<{ end_at: string; status: string }>(`/public/rooms/${token}/book`, {
        minutes,
        subject: "Meeting",
      }),
    onSuccess: (result) => {
      setError(null);
      setBooked({ end_at: result.end_at, pending: result.status === "pending_approval" });
      void query.refetch();
    },
    onError: (caught) => setError(caught instanceof ApiError ? caught : null),
  });

  if (loading || query.isPending) {
    return (
      <div className="qr-page">
        <Lockup height={26} />
        <SkeletonRows rows={3} height={80} />
      </div>
    );
  }
  if (query.isError) {
    return (
      <div className="qr-page">
        <Lockup height={26} />
        <ErrorState error={query.error} onRetry={() => query.refetch()} />
      </div>
    );
  }

  const data = query.data!;
  const tone: Record<string, "available" | "soon" | "occupied" | "offline"> = {
    available: "available", soon: "soon", occupied: "occupied", offline: "offline",
  };

  return (
    <div className="qr-page">
      <div className="row" style={{ justifyContent: "space-between" }}>
        <Lockup height={26} />
        {user && (
          <Link className="btn btn--ghost btn--compact" to="/">
            All rooms
          </Link>
        )}
      </div>

      <section className="qr-hero">
        <div className="row" style={{ justifyContent: "space-between", gap: "var(--space-3)" }}>
          <div className="stack" style={{ gap: 2 }}>
            <h1 className="t-h1">{data.room.name}</h1>
            <p className="t-body ink-secondary">
              {data.room.capacity} seats · {data.room.floor ?? data.room.location_name}
            </p>
          </div>
          <StatusPill tone={tone[data.display.status_token]}>{data.display.label}</StatusPill>
        </div>
        <p className="t-body-l">{data.display.subline}</p>

        {data.room.amenities.length > 0 && (
          <div className="chips">
            {data.room.amenities.map((amenity) => (
              <span className="amenity" key={amenity}>
                <Icon name="check" size={13} />
                {amenity}
              </span>
            ))}
          </div>
        )}
      </section>

      {booked ? (
        <Notice
          tone={booked.pending ? "info" : "success"}
          title={booked.pending ? "Request sent for approval" : "The room is yours"}
        >
          {booked.pending
            ? "An administrator will review your request and you will be notified."
            : `Booked until ${formatTime(booked.end_at, data.room.timezone)}. You are checked in already.`}
          <div style={{ marginTop: "var(--space-3)" }}>
            <Link className="btn btn--secondary btn--compact" to="/bookings">
              My bookings
            </Link>
          </div>
        </Notice>
      ) : data.sign_in_required ? (
        <div className="card">
          <div className="card__body stack" style={{ gap: "var(--space-4)" }}>
            <p className="t-body">
              Sign in with your work account to book this room from your phone.
            </p>
            <Link className="btn btn--primary btn--block btn--touch"
              to={`/sign-in?next=${encodeURIComponent(window.location.pathname)}`}>
              Sign in to book
            </Link>
          </div>
        </div>
      ) : !data.booking_enabled ? (
        <Notice tone="info" title="Mobile booking is turned off for this room">
          Book it from the employee portal or your calendar instead.
        </Notice>
      ) : (
        <div className="card">
          <div className="card__body stack" style={{ gap: "var(--space-4)" }}>
            <p className="t-label">Book it now for</p>
            {error && (
              <Notice tone="warning" title="That is not possible right now">
                {error.message}
              </Notice>
            )}
            <div className="duration-grid">
              {data.quick_durations.map((minutes) => (
                <button key={minutes} type="button" className="btn btn--secondary"
                  disabled={book.isPending} onClick={() => book.mutate(minutes)}>
                  {minutes < 60 ? `${minutes} min` : `${minutes / 60} h`}
                </button>
              ))}
            </div>
            <p className="field__hint">
              You are booking from the door, so you are checked in automatically.
            </p>
          </div>
        </div>
      )}

      <section className="stack" style={{ gap: "var(--space-2)" }}>
        <p className="t-label">Booked today</p>
        {data.busy.length === 0 ? (
          <p className="t-body ink-secondary">Nothing booked — free all day.</p>
        ) : (
          <ul className="stack" style={{ gap: "var(--space-2)" }}>
            {data.busy.map((slot) => (
              <li key={slot.start_at} className="row card" style={{
                padding: "var(--space-3) var(--space-4)", gap: "var(--space-3)",
              }}>
                <span className="t-mono">
                  {formatTime(slot.start_at, data.room.timezone)}–
                  {formatTime(slot.end_at, data.room.timezone)}
                </span>
                <span className="t-body ink-tertiary">Reserved</span>
              </li>
            ))}
          </ul>
        )}
        <p className="t-caption ink-tertiary">
          Meeting titles are not shown on this page.
        </p>
      </section>

      <div style={{ marginTop: "auto" }}>
        <Endorsement />
      </div>
    </div>
  );
}
